import { useMemo, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Building2, Loader2, Search } from 'lucide-react';
import { toast } from 'sonner';
import { api } from '@/api/client';
import { Modal } from '@/components/ui/Modal';

type Merchant = { id: number; companyName?: string | null; brandName?: string | null };

/**
 * Move this order to a different merchant.
 *
 * Renders NOTHING for anyone who is not allowed to — the server answers that on
 * `/orders/merchant-reassign/allowed`, checked against the live user row, so the control cannot be
 * unlocked by editing a role or by hand-editing the bundle. That answer is a convenience for the
 * screen only: `PATCH /orders/:id/merchant` checks the same thing again before it writes anything.
 *
 * Every change is recorded in the order's Edit History card (who, when, from which merchant to
 * which, from what IP, and the reason if one was given) — internal staff only, never the merchant.
 */
export function ChangeMerchantButton({
  orderId,
  orderNo,
  currentMerchantId,
  currentMerchantName,
}: {
  orderId: string | number;
  orderNo?: string | null;
  currentMerchantId?: number | null;
  currentMerchantName?: string | null;
}) {
  const qc = useQueryClient();
  const [open, setOpen] = useState(false);
  const [search, setSearch] = useState('');
  const [picked, setPicked] = useState<number | ''>('');
  const [reason, setReason] = useState('');

  const { data: gate } = useQuery({
    queryKey: ['order-merchant-reassign-allowed'],
    queryFn: async () => (await api.get<{ allowed: boolean }>('/orders/merchant-reassign/allowed')).data,
    staleTime: 10 * 60_000,
    retry: false,
  });

  const { data: merchants = [] } = useQuery({
    queryKey: ['merchants-for-reassign'],
    queryFn: async () => (await api.get('/merchants', { params: { pageSize: 1000 } })).data as any,
    select: (raw: any) => (Array.isArray(raw?.items) ? raw.items : Array.isArray(raw) ? raw : []) as Merchant[],
    staleTime: 5 * 60_000,
    enabled: open,
  });

  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase();
    const rows = (merchants as Merchant[]).filter((m) => m.id !== Number(currentMerchantId));
    if (!q) return rows;
    return rows.filter((m) =>
      `${m.id} ${m.companyName ?? ''} ${m.brandName ?? ''}`.toLowerCase().includes(q));
  }, [merchants, search, currentMerchantId]);

  const save = useMutation({
    mutationFn: async () =>
      (await api.patch(`/orders/${orderId}/merchant`, { merchantId: picked, reason: reason.trim() || undefined })).data,
    onSuccess: (r: any) => {
      toast.success(r?.message ?? 'Merchant changed');
      qc.invalidateQueries({ queryKey: ['order', String(orderId)] });
      qc.invalidateQueries({ queryKey: ['order-edit-logs', String(orderId)] });
      qc.invalidateQueries({ predicate: (q: any) => Array.isArray(q.queryKey) && q.queryKey[0] === 'orders' });
      setOpen(false); setPicked(''); setReason(''); setSearch('');
    },
    onError: (e: any) => toast.error(e?.response?.data?.message ?? 'Could not change the merchant'),
  });

  if (!gate?.allowed) return null;

  return (
    <>
      <button type="button" onClick={() => setOpen(true)}
        className="ml-2 text-[11px] px-1.5 py-0.5 rounded border border-slate-300 text-slate-600 hover:bg-slate-100 align-middle">
        Change
      </button>

      <Modal isOpen={open} onClose={() => setOpen(false)} title="Change merchant"
        subtitle={`${orderNo ?? `Order ${orderId}`} — currently with ${currentMerchantName ?? '—'}`} maxWidth="lg">
        <div className="space-y-3">
          <div className="rounded-lg bg-amber-50 border border-amber-200 p-2.5 text-[11px] text-amber-900 leading-relaxed">
            This moves the whole shipment to another account. Freight not yet invoiced, remittance not
            yet paid and open weight disputes move with it; anything already settled stays with the
            current merchant and is listed back to you. The change is recorded in this order's Edit
            History with your name and the time.
          </div>

          <div>
            <label className="block text-xs font-medium text-slate-600 mb-1">Move to merchant *</label>
            <div className="relative mb-1.5">
              <Search size={14} className="absolute left-2.5 top-1/2 -translate-y-1/2 text-slate-400" />
              <input value={search} onChange={(e) => { setSearch(e.target.value); setPicked(''); }}
                placeholder="Search by name or id" className="input pl-8" />
            </div>
{/*
              A list of buttons, not a <select size>. A select always falls back to selecting its
              FIRST option when its value matches none — and a selection the browser makes on its own
              fires no change event. So narrowing the search to a single merchant left that row
              looking chosen while React still held nothing, and "Change merchant" stayed disabled
              with no way to enable it. Every row here is a real click that always sets the state.
            */}
            <div role="listbox" aria-label="Merchants"
              className="border border-slate-300 rounded-lg divide-y divide-slate-100 max-h-56 overflow-y-auto bg-white">
              {filtered.length === 0 && (
                <div className="px-3 py-6 text-center text-sm text-slate-400">
                  No merchant matches “{search}”
                </div>
              )}
              {filtered.map((m) => {
                const on = picked === m.id;
                return (
                  <button type="button" key={m.id} role="option" aria-selected={on}
                    onClick={() => setPicked(m.id)}
                    className={`w-full text-left px-3 py-2 text-sm flex items-baseline gap-2 transition-colors ${
                      on ? 'bg-primary text-white' : 'hover:bg-slate-50 text-slate-700'}`}>
                    <span className={`font-mono text-xs ${on ? 'text-white/80' : 'text-slate-400'}`}>#{m.id}</span>
                    <span className="font-medium">{m.companyName}</span>
                    {m.brandName && (
                      <span className={on ? 'text-white/75' : 'text-slate-400'}>· {m.brandName}</span>
                    )}
                  </button>
                );
              })}
            </div>
          </div>

          <div>
            <label className="block text-xs font-medium text-slate-600 mb-1">Reason (optional — kept in the history)</label>
            <input value={reason} onChange={(e) => setReason(e.target.value)}
              placeholder="e.g. booked under the wrong client" className="input" />
          </div>

          <div className="flex justify-end gap-2 pt-1 border-t border-slate-100">
            <button type="button" onClick={() => setOpen(false)}
              className="px-4 py-2 text-sm text-slate-600 hover:bg-slate-100 rounded-lg">Cancel</button>
            <span className="mr-auto text-xs text-slate-400 self-center">
              {picked === '' ? 'Choose a merchant from the list above' : `Moving to #${picked}`}
            </span>
            <button type="button" onClick={() => save.mutate()} disabled={picked === '' || save.isPending}
              className="px-4 py-2 bg-primary text-white text-sm rounded-lg font-medium hover:bg-primary-700 disabled:opacity-60 inline-flex items-center gap-2">
              {save.isPending ? <Loader2 size={14} className="animate-spin" /> : <Building2 size={14} />}
              Change merchant
            </button>
          </div>
        </div>
      </Modal>
    </>
  );
}
